import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Panel, StyledSelect, TextField, Label, PrimaryButton, GhostButton, StatusBadge, Chip } from "@/components/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Search, Download, Loader2, ChevronLeft, ChevronRight } from "lucide-react";

function csvCell(v) {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

const EMPTY_OBJ = {};
const EMPTY_ARR = [];

export default function ResourceScreen({ config }) {
  const { hasPerm } = useAuth();
  const { endpoint, module, columns, fields, idField = "id", readOnly = false } = config;
  // Use stable references to avoid infinite re-render/reload loops when config
  // omits these optional keys (defaulting to a fresh {} each render made
  // buildParams -> load -> useEffect trigger endlessly).
  const filters = config.filters || EMPTY_ARR;
  const refs = config.refs || EMPTY_OBJ;
  const fixedFilter = config.fixedFilter || EMPTY_OBJ;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filterVals, setFilterVals] = useState({});
  const [loading, setLoading] = useState(true);
  const [refData, setRefData] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const canCreate = !readOnly && hasPerm(`${module}:create`);
  const canEdit = !readOnly && hasPerm(`${module}:edit`);
  const canDelete = !readOnly && hasPerm(`${module}:delete`);

  const buildParams = useCallback((extra = {}) => {
    const p = new URLSearchParams({ page: String(page), page_size: "15", ...extra });
    if (search) p.set("search", search);
    Object.entries({ ...fixedFilter, ...filterVals }).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  }, [page, search, filterVals, fixedFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${endpoint}?${buildParams()}`);
      setRows(data.data); setTotal(data.total); setPages(data.pages);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [endpoint, buildParams]);

  useEffect(() => {
    Object.entries(refs).forEach(([name, ep]) => {
      api.get(`${ep}?all=1`).then((r) => setRefData((prev) => ({ ...prev, [name]: r.data }))).catch(() => {});
    });
  }, [endpoint]); // reload refs when resource changes

  useEffect(() => { load(); }, [load]);

  const refLabel = (name, id, labelKey = "name") => {
    const list = refData[name] || [];
    const found = list.find((x) => x.id === id);
    return found ? (found[labelKey] || found.name || found.code) : "—";
  };

  const openCreate = () => {
    const init = {};
    fields.forEach((f) => {
      if (f.default !== undefined) init[f.key] = f.default;
      else if (f.type === "boolean") init[f.key] = false;
      else if (f.key === "status") init[f.key] = "active";
      else init[f.key] = "";
    });
    if (!("status" in init)) init.status = "active";
    Object.assign(init, fixedFilter);
    setForm(init); setEditing(null); setOpen(true);
  };
  const openEdit = (row) => { setForm({ ...row }); setEditing(row); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, ...fixedFilter };
    try {
      if (editing) await api.put(`${endpoint}/${editing[idField]}`, payload);
      else await api.post(endpoint, payload);
      toast.success(editing ? "Saved" : "Created");
      setOpen(false); await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (row) => {
    const label = row.code || row.sku || row.name || row.po_number || row.receipt_number
      || row.production_number || row.shipment_number || row.delivery_number || "this record";
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    try { await api.delete(`${endpoint}/${row[idField]}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get(`${endpoint}?${buildParams({ all: "1" })}`);
      const header = columns.map((c) => c.label).join(",");
      const lines = data.map((r) => columns.map((c) => csvCell(c.ref ? refLabel(c.ref, r[c.key], c.refLabel) : r[c.key])).join(","));
      const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${config.key}_export.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.length} rows`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const optionsFor = (f) => {
    let list = refData[f.ref] || [];
    if (f.filterBy && form[f.filterBy.formField]) list = list.filter((o) => o[f.filterBy.optionField] === form[f.filterBy.formField]);
    return list;
  };

  return (
    <div className="space-y-4" data-testid={`resource-${config.key}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <TextField placeholder={`Search ${config.title.toLowerCase()}…`} value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9" data-testid={`${config.key}-search`} />
        </div>
        {filters.map((f) => (
          <div key={f.key} className="min-w-[160px]">
            <StyledSelect value={filterVals[f.key] || ""} onChange={(e) => { setFilterVals({ ...filterVals, [f.key]: e.target.value }); setPage(1); }} data-testid={`${config.key}-filter-${f.key}`}>
              <option value="">{f.label}: All</option>
              {(f.options || (refData[f.ref] || []).map((o) => ({ value: o.id, label: o[f.refLabel || "name"] }))).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </StyledSelect>
          </div>
        ))}
        <GhostButton onClick={exportCsv} data-testid={`${config.key}-export`}><Download className="h-4 w-4" /> CSV</GhostButton>
        {canCreate && <PrimaryButton onClick={openCreate} data-testid={`${config.key}-add`}><Plus className="h-4 w-4" /> New</PrimaryButton>}
      </div>

      <Panel className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={`${config.key}-table`}>
          <thead><tr className="text-left font-mono text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
            {columns.map((c) => <th key={c.key} className="px-4 py-3">{c.label}</th>)}
            <th className="px-4 py-3 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center"><Loader2 className="h-5 w-5 animate-spin text-orange-500 inline" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-slate-500">No records found.</td></tr>
            ) : rows.map((r) => (
              <tr key={r[idField]} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`${config.key}-row`}>
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-3 text-slate-300">
                    {c.key === "status" ? <StatusBadge status={r.status || "active"} />
                      : c.ref ? <Chip>{refLabel(c.ref, r[c.key], c.refLabel)}</Chip>
                      : c.boolean ? (r[c.key] ? "Yes" : "No")
                      : (r[c.key] ?? "—")}
                  </td>
                ))}
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {canEdit && <button onClick={() => openEdit(r)} className="text-slate-400 hover:text-orange-400 mr-3" data-testid={`${config.key}-edit`}><Pencil className="h-4 w-4 inline" /></button>}
                  {canDelete && <button onClick={() => del(r)} className="text-slate-400 hover:text-rose-400" data-testid={`${config.key}-delete`}><Trash2 className="h-4 w-4 inline" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <div className="flex items-center justify-between text-xs text-slate-500 font-mono">
        <span>{total} records</span>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-1.5 border border-slate-700 rounded disabled:opacity-40" data-testid={`${config.key}-prev`}><ChevronLeft className="h-4 w-4" /></button>
          <span>Page {page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="p-1.5 border border-slate-700 rounded disabled:opacity-40" data-testid={`${config.key}-next`}><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`${config.key}-dialog`}>
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-wide">{editing ? "Edit" : "New"} {config.singular}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {fields.filter((f) => !(f.key in fixedFilter)).map((f) => (
              <div key={f.key}>
                <Label>{f.label}{f.required ? " *" : ""}</Label>
                <div className="mt-1.5">
                  {f.type === "boolean" ? (
                    <label className="flex items-center gap-2 text-sm text-slate-300">
                      <input type="checkbox" checked={!!form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })} data-testid={`field-${f.key}`} className="accent-orange-500 h-4 w-4" />
                      {f.hint || "Enabled"}
                    </label>
                  ) : f.type === "select" ? (
                    <StyledSelect value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} required={f.required} data-testid={`field-${f.key}`}>
                      <option value="">— select —</option>
                      {(f.options || optionsFor(f).map((o) => ({ value: o[f.valueKey || "id"], label: o[f.labelKey || "name"] || o.code }))).map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </StyledSelect>
                  ) : f.type === "textarea" ? (
                    <textarea value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} rows={2} data-testid={`field-${f.key}`}
                      className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-orange-500" />
                  ) : (
                    <TextField type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} step="any" required={f.required}
                      value={form[f.key] ?? ""} data-testid={`field-${f.key}`}
                      onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value })} />
                  )}
                </div>
              </div>
            ))}
            <DialogFooter>
              <GhostButton type="button" onClick={() => setOpen(false)}>Cancel</GhostButton>
              <PrimaryButton type="submit" disabled={saving} data-testid={`${config.key}-submit`}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {editing ? "Save" : "Create"}</PrimaryButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
